/**
 * RescheduleEpisodeForm — a new date and start time for an episode, used in the
 * Reschedule modal on the Live Room's Schedules tab. The episode keeps its
 * length; reservations, its chat and links already sent carry over
 * (server/api/episodes/reschedule.js).
 */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';

import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import * as validators from '../../util/validators';
import { Form, FieldTextInput, SecondaryButton, PrimaryButton } from '../../components';

import css from './RescheduleEpisodeForm.module.css';

// "YYYY-MM-DDTHH:mm" in the browser's time zone, as datetime-local expects.
export const toLocalInputValue = date => {
  const d = new Date(date);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

const inFuture = message => value => {
  const t = value ? new Date(value).getTime() : NaN;
  return Number.isFinite(t) && t > Date.now() ? undefined : message;
};

const RescheduleEpisodeFormComponent = props => (
  <FinalForm
    {...props}
    render={fieldRenderProps => {
      const {
        className,
        rootClassName,
        handleSubmit,
        intl,
        invalid,
        inProgress,
        submitError,
        reservedCount,
        onCancel,
      } = fieldRenderProps;

      const classes = classNames(rootClassName || css.root, className);
      const startRequired = validators.required(
        intl.formatMessage({ id: 'RescheduleEpisodeForm.startRequired' })
      );
      const startInFuture = inFuture(
        intl.formatMessage({ id: 'RescheduleEpisodeForm.startInPast' })
      );

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <FieldTextInput
            id="RescheduleEpisodeForm.start"
            name="start"
            type="datetime-local"
            label={intl.formatMessage({ id: 'RescheduleEpisodeForm.startLabel' })}
            validate={validators.composeValidators(startRequired, startInFuture)}
          />
          <p className={css.hint}>
            <FormattedMessage
              id={
                reservedCount > 0
                  ? 'RescheduleEpisodeForm.hintReserved'
                  : 'RescheduleEpisodeForm.hint'
              }
              values={{ count: reservedCount }}
            />
          </p>

          {submitError ? (
            <p className={css.error}>
              <FormattedMessage id="RescheduleEpisodeForm.submitFailed" />
            </p>
          ) : null}

          <div className={css.actions}>
            <SecondaryButton type="button" onClick={onCancel} disabled={inProgress}>
              <FormattedMessage id="RescheduleEpisodeForm.cancel" />
            </SecondaryButton>
            <PrimaryButton type="submit" inProgress={inProgress} disabled={invalid || inProgress}>
              <FormattedMessage id="RescheduleEpisodeForm.submit" />
            </PrimaryButton>
          </div>
        </Form>
      );
    }}
  />
);

RescheduleEpisodeFormComponent.defaultProps = {
  className: null,
  rootClassName: null,
  inProgress: false,
  submitError: null,
  reservedCount: 0,
};

const { bool, func, string, number } = PropTypes;

RescheduleEpisodeFormComponent.propTypes = {
  className: string,
  rootClassName: string,
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
  onCancel: func.isRequired,
  inProgress: bool,
  submitError: PropTypes.any,
  reservedCount: number,
};

const RescheduleEpisodeForm = compose(injectIntl)(RescheduleEpisodeFormComponent);
RescheduleEpisodeForm.displayName = 'RescheduleEpisodeForm';

export default RescheduleEpisodeForm;
